import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Radio as RadioIcon } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useRadioContext } from "@/hooks/useRadioContext";
import { resolveSiteRadioChannel } from "@/lib/radio";
import { setSelectedRadioSiteId } from "@/lib/radio/radio-site-selection";
import { RadioLivePanel } from "@/components/messages/RadioLivePanel";

export const Route = createFileRoute("/_authenticated/radio")({
  head: () => ({
    meta: [
      { title: "Radio | MissionReady" },
      { name: "description", content: "Push-to-talk site Radio for your security team." },
      { property: "og:title", content: "Radio | MissionReady" },
      { property: "og:description", content: "Push-to-talk site Radio for your security team." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: RadioPage,
});

function RadioPage() {
  const { currentOrg } = useAuth() as any;
  const { activeSite, authorizedSites, canSelectSite, loading } = useRadioContext();
  const [channelId, setChannelId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const orgId = currentOrg?.id ?? null;
  const siteId = activeSite?.id ?? null;

  useEffect(() => {
    setChannelId(null);
    setError(null);
    if (!orgId || !siteId) return;
    let cancelled = false;
    resolveSiteRadioChannel(orgId, siteId)
      .then((id) => !cancelled && setChannelId(id))
      .catch(() => !cancelled && setError("You're not authorized for this site's Radio"));
    return () => {
      cancelled = true;
    };
  }, [orgId, siteId]);

  return (
    <div className="mx-auto max-w-3xl space-y-4 p-4 md:p-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-semibold">
            <RadioIcon className="h-5 w-5 text-primary" /> Radio
          </h1>
          <p className="text-sm text-muted-foreground">
            Hold to talk to everyone on your site. Transcripts are saved in{" "}
            <Link to="/messages" className="underline">Messages</Link>; audio is never stored.
          </p>
        </div>
        {canSelectSite && authorizedSites.length > 0 && (
          <select
            aria-label="Radio site"
            className="rounded-md border border-border bg-background px-3 py-2 text-sm"
            value={siteId ?? ""}
            onChange={(e) => orgId && setSelectedRadioSiteId(orgId, e.target.value || null)}
          >
            <option value="">Choose a site…</option>
            {authorizedSites.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        )}
      </header>

      <div className="overflow-hidden rounded-lg border border-border bg-card">
        {loading ? (
          <p className="p-4 text-sm text-muted-foreground">Loading your sites…</p>
        ) : !siteId ? (
          <p className="p-4 text-sm text-muted-foreground">
            {canSelectSite ? "Choose a site to join its Radio." : "No Radio site — you need an active site assignment."}
          </p>
        ) : error ? (
          <p className="p-4 text-sm text-destructive">{error}</p>
        ) : !channelId ? (
          <p className="p-4 text-sm text-muted-foreground">Connecting…</p>
        ) : (
          <RadioLivePanel channelId={channelId} siteId={siteId} siteName={activeSite?.name ?? "Site"} />
        )}
      </div>

      <p className="text-xs text-muted-foreground">
        Radio audio powered by{" "}
        <a href="https://livekit.io/" target="_blank" rel="noopener noreferrer" className="underline">LiveKit</a>
        {" "}· Transcription by{" "}
        <a href="https://deepgram.com/" target="_blank" rel="noopener noreferrer" className="underline">Deepgram</a>
      </p>
    </div>
  );
}
